export type PortfolioPEBasis = 'Standalone' | 'Consolidated';

export type PortfolioPEExclusionReason =
    | 'zero_position_mv'
    | 'no_raw_company_mapping'
    | 'unclassified_sector'
    | 'insufficient_ttm_quarters'
    | 'no_share_count_data'
    | 'negative_or_zero_pat'
    | 'user_excluded';

/** View-independent per-company fundamentals — the same regardless of which strategy
 * (or the pooled book) holds the company. Keyed by company_id in PortfolioPEData.companies. */
export interface PortfolioPECompanyFundamentals {
    name: string | null;
    symbol: string | null;
    sector: string | null;
    basis: PortfolioPEBasis | null;
    quarters_used: number;
    quarters_required: number;
    /** True when fewer than quarters_required quarters of actual PAT data were
     * available, so ttm_pat/company_pe are run-rated (averaged and scaled up) rather
     * than a real reported trailing-twelve-months figure — must be flagged in the UI. */
    is_runrate: boolean;
    ttm_pat: string | null;
    market_cap: string | null;
    company_pe: string | null;
}

/** View-specific fields for one company: how much of it a given view holds, and whether
 * that holding counts toward that view's weighted P/E. */
export interface PortfolioPEPosition {
    company_id: number;
    position_mv: string;
    weight: string | null;
    included: boolean;
    exclusion_reason: PortfolioPEExclusionReason | null;
}

/** A company row joined from a view's position and the shared fundamentals — the shape
 * the table renders, built client-side via joinPositions(). */
export interface PortfolioPECompanyRow extends PortfolioPECompanyFundamentals, PortfolioPEPosition {}

export interface PortfolioPEView {
    portfolio_pe: string | null;
    companies_included: number;
    companies_excluded: number;
    positions: PortfolioPEPosition[];
}

export const POOLED_VIEW_KEY = 'pooled';

/** Add new pooled variants here (e.g. 'pooled_long_horizon') as they're introduced. */
export type PortfolioPEPooledCode = typeof POOLED_VIEW_KEY;

export type PortfolioPEStrategyCode = 'PMS_IEF' | 'AIF_IEF' | 'IFSC_AIF_IEF';

export type PortfolioPEViewKey = PortfolioPEPooledCode | PortfolioPEStrategyCode;

/** Keep in sync with PortfolioPEPooledCode — every pooled variant must be listed here. */
export const PORTFOLIO_PE_POOLED_VIEW_KEYS: PortfolioPEPooledCode[] = [POOLED_VIEW_KEY];

export const isPooledViewKey = (key: PortfolioPEViewKey): key is PortfolioPEPooledCode =>
    (PORTFOLIO_PE_POOLED_VIEW_KEYS as PortfolioPEViewKey[]).includes(key);

export interface PortfolioPEData {
    date: string;
    method: 'harmonic';
    companies: Record<string, PortfolioPECompanyFundamentals>;
    views: Record<PortfolioPEViewKey, PortfolioPEView>;
}

export interface PortfolioPEApiResponse {
    data: PortfolioPEData | null;
    message?: string;
    error?: string;
    meta?: { access_restricted?: boolean };
}

export const PORTFOLIO_PE_EXCLUSION_LABELS: Record<PortfolioPEExclusionReason, string> = {
    zero_position_mv: 'Zero holding',
    no_raw_company_mapping: 'No financial data mapping',
    // Wire key unchanged (a cross-repo contract with raw), but it no longer means the
    // company has no sector — `sector` is populated on these rows. It means raw has no
    // adjusted-PAT formula for it: only Bank, NBFC and General/Insurance are covered.
    unclassified_sector: 'No earnings formula defined',
    insufficient_ttm_quarters: 'Insufficient TTM data',
    no_share_count_data: 'No share count data',
    negative_or_zero_pat: 'Negative/zero earnings',
    user_excluded: 'Excluded by user',
};

export const PORTFOLIO_PE_VIEW_LABELS: Record<PortfolioPEViewKey, string> = {
    pooled: 'Pooled (CORE)',
    PMS_IEF: 'PMS',
    AIF_IEF: 'AIF',
    IFSC_AIF_IEF: 'IFSC AIF',
};

export const PORTFOLIO_PE_VIEW_ORDER: PortfolioPEViewKey[] = [
    POOLED_VIEW_KEY, 'PMS_IEF', 'AIF_IEF', 'IFSC_AIF_IEF',
];

/** Join one view's positions with the shared company fundamentals into full display rows. */
export const joinPositions = (
    view: PortfolioPEView | undefined,
    companies: Record<string, PortfolioPECompanyFundamentals>,
): PortfolioPECompanyRow[] => {
    if (!view) return [];
    return view.positions.map(position => ({
        ...companies[String(position.company_id)],
        ...position,
    }));
};
